// lazyWithRetry — the durable fix for "every page crashed after a deploy."
//
// The app code-splits into 170+ lazy chunks. Each time we ship, the chunk
// filenames get new content hashes. A browser tab still running the PREVIOUS
// build has an index.html that points at chunk files that no longer exist on
// the CDN — so the instant the user opens a lazy route (Phone, Growth, People,
// anything React.lazy), the dynamic import() rejects with a ChunkLoadError and
// the nearest error boundary shows "This tab crashed." To the user it looks
// like the whole app broke; really they're just on a stale bundle.
//
// This wrapper catches that specific failure and does a ONE-TIME full reload,
// which pulls the fresh index.html + current chunks and lands the user exactly
// where they were. A sessionStorage guard prevents an infinite reload loop if
// the chunk is genuinely, permanently missing (then we rethrow so the real
// error surfaces instead of a reload storm).
//
// Drop-in replacement for React.lazy — identical signature.

import React from 'react';

const RETRY_KEY = 'sfa_chunk_reload_once';

const isChunkError = (err) => {
  const msg = String(err && (err.message || err)) || '';
  return (
    err?.name === 'ChunkLoadError' ||
    /Loading chunk [\d]+ failed/i.test(msg) ||
    /Loading CSS chunk/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg)
  );
};

export function lazyWithRetry(factory) {
  return React.lazy(async () => {
    try {
      const mod = await factory();
      // Clean success on the fresh bundle — clear the guard so a FUTURE deploy
      // is allowed its own one-time reload.
      try { window.sessionStorage.removeItem(RETRY_KEY); } catch { /* ignore */ }
      return mod;
    } catch (err) {
      if (isChunkError(err)) {
        let alreadyTried = false;
        try { alreadyTried = !!window.sessionStorage.getItem(RETRY_KEY); } catch { /* ignore */ }
        if (!alreadyTried) {
          try { window.sessionStorage.setItem(RETRY_KEY, String(Date.now())); } catch { /* ignore */ }
          // Force the fresh index.html + current chunks. ignoreCache-style hard
          // reload isn't available cross-browser from JS; a normal reload picks
          // up the new HTML which references the current chunk names.
          window.location.reload();
          // Keep the component suspended for the instant before navigation.
          return new Promise(() => {});
        }
      }
      // Not a chunk error, or we already reloaded once — let the real error
      // reach the boundary so it isn't masked as an endless reload.
      throw err;
    }
  });
}

export default lazyWithRetry;
